import { ActionCreator, Action } from "redux"
import { ThunkDispatch } from "redux-thunk"
import { v4 as uuidv4 } from "uuid"

import * as API from "../../api"
import { apiCache } from "../../services/api-cache"
import { ThunkResult, ApplicationState } from "../../../core/types"
import { InAppCommsActionTypes } from "./in-app-comms-types"
import {
    DeliveryPayload,
    DeliveryPreferences,
    InteractionEvent,
    InAppEventType,
    SubscriptionLevel,
} from "../../model/in-app-comms-model"
import { startInAppCommsPoller, stopInAppCommsPoller } from "./in-app-comms-polling"
import { pendingRetryTimers, cancelPendingRetries } from "./in-app-comms-retry"
import {
    DeliveryFetchError,
    deliveryHttpError,
    getHttpStatus,
    logDeliveryFailure,
    toDeliveryFetchError,
} from "./in-app-comms-logging"

export { cancelPendingRetries }

export const RETRY_DELAYS_MS = [5_000, 15_000, 60_000]
const MAX_RETRY_ATTEMPTS = 10

// HTTP 501 Not Implemented — the SL server has no in-app-comms delivery layer
// configured. We treat this as "feature unavailable" rather than a transient
// error, so the UI degrades gracefully instead of showing error/loading states.
const HTTP_NOT_IMPLEMENTED = 501
const HTTP_UNAUTHORIZED = 401

// --- Retry helper (not exported — internal to the event service) ---

function scheduleEventQueueRetry(
    attempt: number,
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>,
    getState: () => ApplicationState
): void {
    if (attempt >= MAX_RETRY_ATTEMPTS) return   // give up rather than retry forever
    const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
    const timerId = setTimeout(async () => {
        pendingRetryTimers.delete(timerId)
        const events = getState().inAppComms.pendingEvents
        if (events.length === 0) return
        try {
            const response = await API.postInAppDeliveryEvents(events)
            if (response.ok) {
                // Only remove events that were in this batch — events queued
                // while the POST was in-flight must not be silently dropped.
                dispatch(dequeueEvents(events.map(e => e.eventId)))
                await (dispatch(fetchDeliveryPayload()) as unknown as Promise<void>)
            } else {
                scheduleEventQueueRetry(attempt + 1, dispatch, getState)
            }
        } catch {
            scheduleEventQueueRetry(attempt + 1, dispatch, getState)
        }
    }, delay)
    pendingRetryTimers.add(timerId)
}

// --- Thunks ---

export const fetchDeliveryPayload = (): ThunkResult<Promise<void>> => async (
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>,
    getState: () => ApplicationState
) => {
    dispatch(apiAttempt())
    // A failure clears on the next success or 501, so an error already in state
    // means this failure is a repeat and has already been logged.
    const reportFailure = (error: DeliveryFetchError) => {
        const isFirstFailure = !getState().inAppComms.error
        dispatch(apiFailure(error))
        if (isFirstFailure) {
            logDeliveryFailure(error)
        }
    }
    // Not configured on this server — stop polling and flag unavailable.
    const markUnavailable = () => {
        stopInAppCommsPoller()
        dispatch(setUnavailable())
    }
    try {
        const slStatus = await apiCache.getSlStatus()
        const response = await API.getInAppDeliveryMessages(slStatus.uuid)
        if (response.ok) {
            dispatch(setDeliveryPayload(response.data))
        } else if (response.status === HTTP_NOT_IMPLEMENTED) {
            markUnavailable()
        } else if (response.status) {
            reportFailure(deliveryHttpError(response.status, response.problem))
        }
        // A resolved not-ok response with no status is the cloud network-error
        // workaround in API.makeCall, which deliberately ignores such errors.
    } catch (err) {
        // API.makeCall rejects with the response for any non-ok status, so a real
        // 501 or 5xx lands here rather than in the branches above.
        const status = getHttpStatus(err)
        if (status === HTTP_NOT_IMPLEMENTED) {
            markUnavailable()
        } else if (status !== HTTP_UNAUTHORIZED) {
            // A 401 is a signed-out or expired session (e.g. the poll that starts
            // before sign-in), not a delivery failure.
            reportFailure(toDeliveryFetchError(err))
        }
    }
}

export const fetchPreferences = (): ThunkResult<Promise<void>> => async (
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>
) => {
    try {
        const response = await API.getInAppDeliveryPreferences()
        if (response.ok) {
            dispatch(setPreferences(response.data))
        } else if (response.status === HTTP_NOT_IMPLEMENTED) {
            dispatch(setUnavailable())
        }
    } catch (err) {
        // API.makeCall rejects for any non-ok status, so a real 501 lands here.
        // Any other preference fetch failure is non-fatal.
        if (getHttpStatus(err) === HTTP_NOT_IMPLEMENTED) {
            dispatch(setUnavailable())
        }
    }
}

export const updatePreferences = (
    scope: "user" | "tenant",
    subscriptionLevel: SubscriptionLevel
): ThunkResult<Promise<void>> => async (
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>
) => {
    await API.putInAppDeliveryPreferences(scope, subscriptionLevel)
    await dispatch(fetchPreferences())
}

export const sendInteractionEvent = (
    inappMessageId: string,
    eventType: InAppEventType
): ThunkResult<Promise<void>> => async (
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>,
    getState: () => ApplicationState
) => {
    let smrtlinkSystemId = ""
    try {
        const slStatus = await apiCache.getSlStatus()
        smrtlinkSystemId = slStatus.uuid
    } catch {
        // best-effort — proceed with empty ID if status is unavailable
    }

    const event: InteractionEvent = {
        eventId: uuidv4(),
        inappMessageId,
        eventType,
        smrtlinkSystemId,
        userIdHash: "", // TODO: derive from user session — see open question in Confluence plan
    }

    try {
        const response = await API.postInAppDeliveryEvents([event])
        if (response.ok) {
            dispatch(fetchDeliveryPayload())
        } else {
            dispatch(queueEvent(event))
            // Only start a new retry chain if one isn't already running —
            // the existing chain will pick up the newly queued event.
            if (pendingRetryTimers.size === 0) {
                scheduleEventQueueRetry(0, dispatch, getState)
            }
        }
    } catch {
        dispatch(queueEvent(event))
        if (pendingRetryTimers.size === 0) {
            scheduleEventQueueRetry(0, dispatch, getState)
        }
    }
}

export const startInAppCommsPolling = (): ThunkResult<void> => (
    dispatch: ThunkDispatch<ApplicationState, undefined, Action>
) => {
    startInAppCommsPoller(() => dispatch(fetchDeliveryPayload()))
}

export const stopInAppCommsPolling = (): ThunkResult<void> => () => {
    stopInAppCommsPoller()
}

// --- Plain action creators ---

export const apiAttempt: ActionCreator<Action> = () => ({
    type: InAppCommsActionTypes.API_ATTEMPT,
})

export const apiFailure: ActionCreator<Action> = (error: Error) => ({
    type: InAppCommsActionTypes.API_FAILURE,
    payload: error,
})

export const setUnavailable: ActionCreator<Action> = () => ({
    type: InAppCommsActionTypes.SET_UNAVAILABLE,
})

export const setDeliveryPayload: ActionCreator<Action> = (payload: DeliveryPayload) => ({
    type: InAppCommsActionTypes.SET_DELIVERY_PAYLOAD,
    payload,
})

export const setPreferences: ActionCreator<Action> = (preferences: DeliveryPreferences) => ({
    type: InAppCommsActionTypes.SET_PREFERENCES,
    payload: preferences,
})

export const queueEvent: ActionCreator<Action> = (event: InteractionEvent) => ({
    type: InAppCommsActionTypes.QUEUE_EVENT,
    payload: event,
})

export const dequeueEvents: ActionCreator<Action> = (eventIds: string[]) => ({
    type: InAppCommsActionTypes.DEQUEUE_EVENTS,
    payload: eventIds,
})

export const clearEventQueue: ActionCreator<Action> = () => ({
    type: InAppCommsActionTypes.CLEAR_EVENT_QUEUE,
})

export const dismissModal: ActionCreator<Action> = (inappMessageId: string) => ({
    type: InAppCommsActionTypes.DISMISS_MODAL,
    payload: inappMessageId,
})
